import React, { useState, useEffect, useCallback } from "react";
import "./HenrriDevisModal.css";

const API_BASE = "https://kanban.amservices.eu/api";

/**
 * Popup de recherche + détail des devis Henrri.
 *
 * Props :
 *   onClose  : () => void          — ferme la popup
 *   onSelect : (devis) => void     — appelé quand on "utilise" un devis (remplit num_devis).
 *                                    Optionnel : si absent, pas de bouton "Utiliser".
 *   search   : string (optionnel)  — préremplit + lance la recherche à l'ouverture
 */
const HenrriDevisModal = ({ onClose, onSelect, search = "", initialDocumentId, documentType = 'Quotation' }) => {
    const [devis, setDevis] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [closing, setClosing] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);

    const [searchTerm, setSearchTerm] = useState(search);
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({ total: 0, total_pages: 1, has_next: false });

    // Vue détail
    const [detail, setDetail] = useState(null);       // { entete, lignes }
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [detailError, setDetailError] = useState(null);

    const selectable = typeof onSelect === "function";

    const fetchDevis = useCallback(async (pageArg, searchArg) => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ page: pageArg, limit: 50 });
            if (searchArg) params.append("search", searchArg);

            const res = await fetch(`${API_BASE}/get_henrri_devis.php?${params.toString()}`);
            const data = await res.json();

            if (data.success) {
                setDevis(data.data);
                setMeta(data.meta || { total: data.data.length, total_pages: 1, has_next: false });
            } else {
                setError(data.error || "Erreur inconnue lors de la récupération des devis.");
            }
        } catch (err) {
            console.error(err);
            setError("Impossible de contacter le serveur.");
        } finally {
            setLoading(false);
            setHasSearched(true);
        }
    }, []);

    const fetchDetail = useCallback(async (id) => {
        setLoadingDetail(true);
        setDetailError(null);
        setDetail(null);
        try {
            const res = await fetch(`${API_BASE}/get_henrri_devis_detail.php?id=${id}`);
            const data = await res.json();
            if (data.success) {
                setDetail({ entete: data.entete, lignes: data.lignes });
            } else {
                setDetailError(data.error || "Impossible de charger le détail du devis.");
            }
        } catch (err) {
            console.error(err);
            setDetailError("Impossible de contacter le serveur.");
        } finally {
            setLoadingDetail(false);
        }
    }, []);

    // Recherche auto à l'ouverture si un terme est fourni
    useEffect(() => {
        if (initialDocumentId) fetchDetail(initialDocumentId);
    }, [initialDocumentId, fetchDetail]);

    useEffect(() => {
        if (search) fetchDevis(1, search);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Rechargement sur changement de page
    useEffect(() => {
        if (hasSearched) fetchDevis(page, searchTerm);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        fetchDevis(1, searchTerm);
    };

    const handleClose = () => {
        setClosing(true);
        setTimeout(() => onClose(), 280);
    };

    const handleRowClick = (d) => {
        // Ouvre le détail du devis
        fetchDetail(d.id);
    };

    const handleUse = () => {
        if (!selectable || !detail) return;
        setClosing(true);
        setTimeout(() => onSelect(detail.entete), 200);
    };

    const fmtMontant = (v) =>
        v == null
            ? "—"
            : parseFloat(v).toLocaleString("fr-FR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
              }) + " €";

    const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

    // ---- VUE DÉTAIL ----
    if (detail || loadingDetail || detailError) {
        return (
            <div className={`modal-overlay ${closing ? "closing" : ""}`}>
                <div className="modal-content henrri-modal">
                    <div className="henrri-modal-header">
                        <h2>📄 {documentType === 'Invoice' ? 'Détail de la facture' : 'Détail du devis'}</h2>
                        <button
                            type="button"
                            className="henrri-back-btn"
                            onClick={() => { setDetail(null); setDetailError(null); }}
                        >← Retour à la liste</button>
                    </div>

                    {loadingDetail ? (
                        <p className="henrri-state">Chargement du détail…</p>
                    ) : detailError ? (
                        <p className="henrri-state henrri-error">⚠️ {detailError}</p>
                    ) : (
                        <>
                            <div className="henrri-detail-head">
                                <div>
                                    <span className="henrri-detail-num mono">{detail.entete.numero || "—"}</span>
                                    <span className={`henrri-badge ${detail.entete.finalise ? "final" : "draft"}`}>
                                        {detail.entete.finalise ? "Finalisé" : "Brouillon"}
                                    </span>
                                    {detail.entete.valide && (
                                        <span className="henrri-badge validated">Validé</span>
                                    )}
                                </div>
                                <div className="henrri-detail-meta">
                                    <div><strong>Client :</strong> {detail.entete.client || "—"}</div>
                                    <div><strong>Date :</strong> {fmtDate(detail.entete.date)}</div>
                                    {detail.entete.titre && (
                                        <div><strong>Objet :</strong> {detail.entete.titre}</div>
                                    )}
                                </div>
                            </div>

                            <div className="henrri-table-wrapper">
                                <table className="henrri-table henrri-lignes">
                                    <thead>
                                        <tr>
                                            <th>Réf.</th>
                                            <th>Description</th>
                                            <th className="ta-right">Qté</th>
                                            <th className="ta-right">PU HT</th>
                                            <th className="ta-right">TVA</th>
                                            <th className="ta-right">Total HT</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detail.lignes.length === 0 ? (
                                            <tr><td colSpan="6" className="henrri-state">Aucune ligne.</td></tr>
                                        ) : (
                                            detail.lignes.map((l) => (
                                                <tr key={l.id} className={l.est_groupe ? "ligne-groupe" : ""}>
                                                    <td className="mono">{l.reference || "—"}</td>
                                                    <td dangerouslySetInnerHTML={{ __html: l.description || "—" }} />
                                                    <td className="ta-right">{l.quantite ?? "—"}</td>
                                                    <td className="ta-right">{fmtMontant(l.prix_ht)}</td>
                                                    <td className="ta-right">{l.tva != null ? l.tva + "%" : "—"}</td>
                                                    <td className="ta-right strong">{fmtMontant(l.total_ht)}</td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="henrri-detail-totaux">
                                <div><span>Total HT</span><strong>{fmtMontant(detail.entete.montant_ht)}</strong></div>
                                <div><span>TVA</span><strong>{fmtMontant(detail.entete.montant_tva)}</strong></div>
                            </div>
                        </>
                    )}

                    <div className="modal-btns">
                        {selectable && detail && (
                            <button type="button" className="btn-add" onClick={handleUse}>
                                Utiliser ce devis
                            </button>
                        )}
                        <button type="button" onClick={handleClose}>Fermer</button>
                    </div>
                </div>
            </div>
        );
    }

    // ---- VUE LISTE ----
    return (
        <div className={`modal-overlay ${closing ? "closing" : ""}`}>
            <div className="modal-content henrri-modal">
                <div className="henrri-modal-header">
                    <h2>📄 Rechercher un devis Henrri</h2>
                    {hasSearched && (
                        <span className="henrri-count">{meta.total} résultat{meta.total > 1 ? "s" : ""}</span>
                    )}
                </div>

                <form onSubmit={handleSearch} className="henrri-search-row">
                    <input
                        type="text"
                        placeholder="Nom du client, numéro de devis…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="henrri-search-input"
                        autoFocus
                    />
                    <button type="submit" className="btn-add henrri-search-btn">Rechercher</button>
                </form>

                <div className="henrri-table-wrapper">
                    {loading ? (
                        <p className="henrri-state">Recherche en cours…</p>
                    ) : error ? (
                        <p className="henrri-state henrri-error">⚠️ {error}</p>
                    ) : !hasSearched ? (
                        <p className="henrri-state">
                            Tapez un nom de client ou un numéro puis « Rechercher ».
                        </p>
                    ) : devis.length === 0 ? (
                        <p className="henrri-state">Aucun devis trouvé.</p>
                    ) : (
                        <table className="henrri-table selectable">
                            <thead>
                                <tr>
                                    <th>N°</th>
                                    <th>Client</th>
                                    <th>Date</th>
                                    <th className="ta-right">Montant HT</th>
                                    <th className="ta-center">Statut</th>
                                </tr>
                            </thead>
                            <tbody>
                                {devis.map((d) => (
                                    <tr
                                        key={d.id}
                                        onClick={() => handleRowClick(d)}
                                        className="row-clickable"
                                        title="Cliquer pour voir le détail"
                                    >
                                        <td className="mono">{d.numero || "—"}</td>
                                        <td>
                                            {d.client || <em>Sans client</em>}
                                            {d.titre && <div className="henrri-titre">{d.titre}</div>}
                                        </td>
                                        <td>{fmtDate(d.date)}</td>
                                        <td className="ta-right strong">{fmtMontant(d.montant_ht)}</td>
                                        <td className="ta-center">
                                            <span className={`henrri-badge ${d.finalise ? "final" : "draft"}`}>
                                                {d.finalise ? "Finalisé" : "Brouillon"}
                                            </span>
                                            {d.valide && <span className="henrri-badge validated">Validé</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {!loading && !error && hasSearched && meta.total_pages > 1 && (
                    <div className="henrri-pagination">
                        <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                            ← Précédent
                        </button>
                        <span>Page {page} / {meta.total_pages}</span>
                        <button disabled={!meta.has_next} onClick={() => setPage((p) => p + 1)}>
                            Suivant →
                        </button>
                    </div>
                )}

                <div className="modal-btns">
                    <button type="button" onClick={handleClose}>Fermer</button>
                </div>
            </div>
        </div>
    );
};

export default HenrriDevisModal;
